import { textDirection, type Translate } from '@destera/i18n';
import type { LauncherMessages } from '@destera/i18n/catalogs/launcher';
import type { WidgetConfig } from '../shared/config.ts';

/**
 * Kapalı balon: çerçevesiz, saf DOM. Sayfaya yük bindirmez (yükleyici bütçesi ≤ 5 KB).
 * Metinleri yükleyiciye gömülü küçük `launcher` kataloğundan gelir; sohbet penceresi kendi
 * kataloğunu ayrıca ve yalnız seçilen dilde indirir. Renkler tema değişkenlerinden (theme.ts).
 */
const ICON =
  '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><path fill="currentColor" d="M12 2.5c-5.5 0-10 3.9-10 8.8 0 2.6 1.3 5 3.4 6.6l-.9 3.7 4.4-2.2c1 .3 2 .4 3.1.4 5.5 0 10-3.9 10-8.8S17.5 2.5 12 2.5z"/><path class="s" d="M12 6.6l1.2 3 3 1.2-3 1.2-1.2 3-1.2-3-3-1.2 3-1.2z"/></svg>';

/** Rozette gösterilen üst sınır; ekran okuyucuya gerçek sayı söylenir. */
const BADGE_MAX = 99;

export const LAUNCHER_CSS = `
:host{all:initial}
.l{position:fixed;bottom:20px;min-width:60px;height:60px;padding:0;border:0;border-radius:30px;cursor:pointer;
color:var(--d-on);background:var(--d-grad);display:flex;align-items:center;justify-content:center;gap:0;z-index:2147483000;
box-shadow:0 12px 28px -8px color-mix(in srgb,var(--d-accent) 70%,transparent),0 2px 6px rgba(17,24,39,.16);
transition:transform .35s cubic-bezier(.34,1.56,.64,1),box-shadow .35s,padding .35s cubic-bezier(.22,1,.36,1);
animation:dp .5s cubic-bezier(.34,1.56,.64,1) both}
.l:hover,.l:focus-visible{transform:translateY(-3px);padding-inline-end:22px;
box-shadow:0 20px 40px -10px color-mix(in srgb,var(--d-accent) 85%,transparent),0 3px 8px rgba(17,24,39,.2)}
.l:active{transform:translateY(-1px) scale(.97)}
.l:focus-visible{outline:3px solid var(--d-accent);outline-offset:3px}
/* Simge sabit kalır; rozet ona tutunur, etiket yanından açılır. */
.i{position:relative;display:grid;place-items:center;width:60px;height:60px;flex:none}
.l .s{fill:var(--d-accent)}
.t{max-width:0;overflow:hidden;white-space:nowrap;opacity:0;font:600 14px/1 system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;
transition:max-width .35s cubic-bezier(.22,1,.36,1),opacity .25s}
.l:hover .t,.l:focus-visible .t{max-width:180px;opacity:1}
.l[hidden]{display:none}.r{right:20px}.left{left:20px}
.b{position:absolute;top:2px;inset-inline-end:2px;min-width:22px;height:22px;box-sizing:border-box;padding:0 6px;
border:2px solid #fff;border-radius:11px;background:#e11d48;color:#fff;font:700 11px/18px system-ui,sans-serif;
animation:dq 2s ease-out infinite}
.b:empty{display:none}
/* Yalnız ekran okuyucuya: okunmamış mesaj duyurusu. */
.sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap}
@keyframes dp{from{opacity:0;transform:scale(.3)}}
@keyframes dq{from{box-shadow:0 0 0 0 rgba(225,29,72,.55)}to{box-shadow:0 0 0 9px rgba(225,29,72,0)}}
@media (prefers-reduced-motion:reduce){.l,.b,.t{transition:none;animation:none}}
/* Dar ekranda etiket açılmaz: baloncuk ekranın yarısını kaplamasın. */
@media (max-width:480px){.l:hover,.l:focus-visible{padding-inline-end:0}.l:hover .t,.l:focus-visible .t{max-width:0;opacity:0}}`;

export type Launcher = {
  show(): void;
  hide(): void;
  setUnread(count: number): void;
  /** Pencere kapanınca odak buraya dönüyor (WCAG 2.4.3). */
  focus(): void;
  /** İlk ziyarette marka sonradan geliyor; görünen etiket yerinde değişiyor, erişilebilir ad değil. */
  setLabel(text: string): void;
  /** Marka sonradan gelince köşe de yerinde değişiyor. */
  setPosition(position: WidgetConfig['position']): void;
};

export function createLauncher(
  root: ShadowRoot,
  config: WidgetConfig,
  t: Translate<LauncherMessages>,
  onOpen: () => void,
): Launcher {
  const number = new Intl.NumberFormat(config.locale);
  const button = document.createElement('button');
  button.type = 'button';
  const place = (position: WidgetConfig['position']) => {
    button.className = `l ${position === 'left' ? 'left' : 'r'}`;
  };
  place(config.position);
  // Arapçada etiket sağdan sola akıyor, rozet de mantıksal kenardan tutunuyor (M1 §3.12.8).
  button.dir = textDirection(config.locale);
  /**
   * Etiket işletmenin kendi metni olabiliyor; yoksa ürünün çevirisi.
   *
   * Ekran okuyucuya giden ad HER ZAMAN ürünün metni: işletme etiketi boş bırakabiliyor ve
   * "Sor" gibi tek kelimelik bir etiket, bağlamı görmeyen biri için düğmenin ne yaptığını
   * söylemiyor. Görünen metin markanın, erişilebilir ad ürünün.
   */
  const label = t('open');
  const shown = config.brand?.launcherLabel ?? label;
  button.setAttribute('aria-label', label);
  /**
   * Rozet GÖRSEL, duyuru ayrı bir bölgede.
   *
   * Sayının kendisine `aria-live` koymak "3" diye okutuyordu; cümle ise düğmenin adında
   * kalıyordu ve ad değişimi duyurulmuyor. İkisini ayırmak, ekran okuyucunun tam cümleyi bir
   * kez duyması demek.
   */
  button.innerHTML =
    `<span class="i">${ICON}<span class="b" aria-hidden="true"></span></span>` +
    `<span class="t"></span><span class="sr" aria-live="polite"></span>`;
  /**
   * Etiket `textContent` ile yazılıyor, HTML olarak DEĞİL.
   *
   * İşletmenin yazdığı bir metin ve `innerHTML` ile basmak, kendi panelinden kendi sitesine
   * betik sokabilen bir yüzey bırakırdı. Kaçışlamak da çözerdi ama kaçışlayıcı yükleyiciye
   * bayt ekliyor; `textContent` hem güvenli hem bedava.
   */
  const labelNode = button.querySelector('.t');
  if (labelNode !== null) labelNode.textContent = shown;
  button.addEventListener('click', onOpen);
  root.append(button);

  const badge = button.querySelector('.b');
  const announce = button.querySelector('.sr');
  return {
    show: () => {
      button.hidden = false;
    },
    hide: () => {
      button.hidden = true;
    },
    setUnread: (count) => {
      if (!badge || !announce) return;
      badge.textContent = count > 0 ? number.format(Math.min(count, BADGE_MAX)) : '';
      announce.textContent = count > 0 ? t('unread', { count }) : '';
    },

    focus: () => button.focus(),
    setLabel: (text) => {
      if (labelNode !== null) labelNode.textContent = text;
    },
    setPosition: place,
  };
}
